import { ref, computed } from 'vue';
import { useScreenOrientation, useWindowSize } from '@vueuse/core';
import { useDisplay } from 'vuetify';
import { storeToRefs } from 'pinia';
import useUiStore from '../stores/ui';

const useBookCoverDimensions = () => {
  const { orientation } = useScreenOrientation();
  const { height } = useWindowSize();
  const { xs, sm, md } = useDisplay();
  const { isLandscape } = storeToRefs(useUiStore());

  const isTinyPhone = computed(() => height.value < 700 && orientation.value.includes('portrait') && xs.value);

  const maxDimensions = computed(() => {
    // iPhone SE and similar very small screens
    if (isTinyPhone.value) return { width: 140, height: 180 };

    if (xs.value || isLandscape.value) return { width: 200, height: 320 };

    if (sm.value || md.value) return { width: 330, height: 430 };

    return { width: 410, height: 525 };
  });

  const coverRef = ref<any>(null);
  const naturalWidth = ref<number | null>(null);
  const naturalHeight = ref<number | null>(null);

  const coverWidth = computed(() => {
    const imageRatios = getImageRatios();

    const { width: maxWidth, height: maxHeight } = maxDimensions.value;

    if (!imageRatios) {
      return maxWidth;
    }

    if (imageRatios.imageRatio > imageRatios.maxRatio) {
      // Image is wider than our box -> fix width, shrink height
      return maxWidth;
    } else {
      // Image is taller than our box -> fix height, shrink width
      return maxHeight * imageRatios.imageRatio;
    }
  });

  const coverHeight = computed(() => {
    const imageRatios = getImageRatios();

    const { width: maxWidth, height: maxHeight } = maxDimensions.value;

    if (!imageRatios) {
      return maxHeight;
    }

    if (imageRatios.imageRatio > imageRatios.maxRatio) {
      // Image is wider than our box -> fix width, shrink height
      return maxWidth / imageRatios.imageRatio;
    } else {
      // Image is taller than our box -> fix height, shrink width
      return maxHeight;
    }
  });

  const getImageRatios = () => {
    if (!naturalWidth.value || !naturalHeight.value) return null;

    const { width: maxWidth, height: maxHeight } = maxDimensions.value;
    const imageRatio = naturalWidth.value / naturalHeight.value;
    const maxRatio = maxWidth / maxHeight;

    return { imageRatio, maxRatio };
  };

  // 2. This runs when the image actually loads
  const onImageLoaded = () => {
    if (!coverRef.value || !coverRef.value.image) return;

    naturalWidth.value = coverRef.value.image.naturalWidth;
    naturalHeight.value = coverRef.value.image.naturalHeight;
  };

  return { coverRef, coverWidth, coverHeight, onImageLoaded };
};

export default useBookCoverDimensions;
